import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { routeSelected, selectBookmark, websocketMessage } from "redux/actions";
import _ from "lodash";
import { Polyline } from "react-leaflet";
import { interpolate, videoKeyToTimestamp, videoTimestampExtractor } from "components/util/PlaylistUtils";
import { calculateDistanceInPlaylist } from "components/util/GeometryUtilsV2";
import { routeGeomSelector, sourceOffsetSelector } from "redux/selectors";
import memoizeOne from "memoize-one";

const MAX_VID_TIME_DIFF = 5;

const sessionIDSelector = (state) => state.playlist.data.routeID;
const currentModeSelector = (state) => state.tool_mode;

const getExportIndices = (segmentSelection) => {
    if (!segmentSelection) {
        return null;
    }
    let segmentStartIndex = segmentSelection.start;
    let segmentEndIndex = segmentSelection.end;

    if (segmentStartIndex === -1 || segmentEndIndex === -1) {
        return null;
    }

    if (segmentStartIndex > segmentEndIndex) {
        let temp = segmentStartIndex;
        segmentStartIndex = segmentEndIndex;
        segmentEndIndex = temp;
    }
    return [segmentStartIndex, segmentEndIndex];
};

const exportSegmentDistanceSelector = (state) => {
    const indicies = getExportIndices(state.segmentSelection);
    if (!indicies) {
        return null;
    }
    const [startIndex, endIndex] = indicies;
    const videoParts = _.get(state.playlist.data, ["video", state.playlist.position.sourceIndex], null);
    if (!videoParts) {
        return null;
    }

    const geometry = routeGeomSelector(state);

    const startVideoKey = _.get(videoParts, [startIndex, 0]);
    const endVideoKey = _.get(videoParts, [endIndex, 0]);

    if (!startVideoKey || !endVideoKey) {
        return null;
    }

    const startTimestamp = videoKeyToTimestamp(startVideoKey);
    const endTimestamp = videoKeyToTimestamp(endVideoKey);

    const startDistance = calculateDistanceInPlaylist(startTimestamp, geometry);
    const endDistance = calculateDistanceInPlaylist(endTimestamp, geometry);

    return [startDistance, endDistance];
};

const shortcutDistancesSelector = (state) => {
    const shortcutPoints = _.get(state.shortcuts.shortcutSessionPoints, [state.playlist.data.routeID], []);

    if (shortcutPoints && shortcutPoints.length) {
        return shortcutPoints[0].sort(function (a, b) {
            return a - b;
        });
    }

    return null;
};

const calculateVideoDistanceRanges = memoizeOne((videoTimestampSections, routeGeometry, sourceOffset) => {
    if (!videoTimestampSections?.length) {
        return [[-Infinity, Infinity]];
    }
    const distanceRanges = [];
    videoTimestampSections.forEach((videoItems) => {
        const distanceVals = [];
        videoItems.forEach((videoTimestamp) => {
            const distanceValue = calculateDistanceInPlaylist(videoTimestamp, routeGeometry, sourceOffset);
            if (distanceValue !== null) {
                distanceVals.push(distanceValue);
            }
        });

        const lowerDistance = Math.min(...distanceVals);
        const upperDistance = Math.max(...distanceVals);

        distanceRanges.push([lowerDistance, upperDistance]);
    });

    return distanceRanges;
});

const calculateVideoSections = memoizeOne((video) => {
    const videoSections = [];
    let lastTimestamp = null;
    let currentSection = [];

    video.forEach((video) => {
        const videoTimestamp = videoTimestampExtractor(video);

        if (!lastTimestamp) {
            lastTimestamp = videoTimestamp;
        }

        if (Math.abs(videoTimestamp - lastTimestamp) > MAX_VID_TIME_DIFF) {
            videoSections.push(currentSection);
            currentSection = [];
        }

        currentSection.push(videoTimestamp);
        lastTimestamp = videoTimestamp;
    });

    if (currentSection.length) {
        videoSections.push(currentSection);
    }

    return videoSections;
});

const videoDistanceSelector = (state) => {
    const sourceIndex = _.get(state, ["playlist", "position", "sourceIndex"]);
    const video = _.get(state.playlist, ["data", "video", sourceIndex], []);

    const videoSections = calculateVideoSections(video);

    const routeGeometry = routeGeomSelector(state);
    const sourceOffset = sourceOffsetSelector(state);

    const distanceRanges = calculateVideoDistanceRanges(videoSections, routeGeometry, sourceOffset);
    return distanceRanges;
};

const SelectedRouteSegmentGeometryV2 = ({ stripped, routeGeomOverrideData = {}, lineColour = "#5dd65f", selectRouteToFront }) => {
    const polylineRefs = useRef([]);
    const routeGeom = useSelector(routeGeomSelector);
    const selectedRouteID = useSelector(sessionIDSelector);
    const currentMode = useSelector(currentModeSelector);
    const exportDistances = useSelector(exportSegmentDistanceSelector);
    const shortcutDistances = useSelector(shortcutDistancesSelector);
    const videoDistanceSections = useSelector(videoDistanceSelector);

    useLayoutEffect(() => {
        // Use layout effect to ensure DOM mutations are complete before bringing polylines to front
        if (polylineRefs.current) {
            polylineRefs.current.forEach((polylineRef) => {
                if (polylineRef.current && polylineRef.current.leafletElement) {
                    polylineRef.current.leafletElement.bringToFront();
                }
            });
        }
    }, [selectRouteToFront]);

    const dispatch = useDispatch();

    const routeCoordinates = useMemo(() => {
        if (!_.isEmpty(routeGeomOverrideData)) {
            return _.get(routeGeomOverrideData, ["distance_to_coordinate"], []);
        }

        const distanceToCoordinate = _.get(routeGeom, ["distance_to_coordinate"], []);
        return distanceToCoordinate;
    }, [routeGeom, routeGeomOverrideData]);

    const onMouseClick = useCallback(
        (location) => {
            if (currentMode) {
                return;
            }

            const latLng = [location.latlng.lng, location.latlng.lat];

            if (stripped) {
                if (stripped === "sidekick") {
                    dispatch(
                        websocketMessage({
                            action: "request_map_position",
                            coords: latLng,
                            sessionID: selectedRouteID,
                        }),
                    );
                }
            }

            dispatch(selectBookmark(null));
            dispatch(routeSelected(selectedRouteID, latLng));
        },
        [currentMode, dispatch, selectedRouteID, stripped],
    );

    const selectedPolyline = useMemo(() => {
        polylineRefs.current = [];

        const allCoordinates = routeCoordinates.flatMap((subsection) => {
            return subsection;
        });

        const sectionsToRender = videoDistanceSections.map((section) => {
            const [startDistance, endDistance] = section;

            let distanceFilteredPoints = [];
            allCoordinates.forEach((point, idx) => {
                const nextPoint = allCoordinates[idx + 1];

                const coordDistance = point[0];
                const nextPointDistance = _.get(nextPoint, [0]);

                let shouldFilterOut = true;
                videoDistanceSections.forEach((range) => {
                    if (nextPoint && coordDistance < endDistance && nextPointDistance > endDistance) {
                        const interpolationRatio = (endDistance - coordDistance) / (nextPointDistance - coordDistance);
                        const interpolatedPoint = interpolate([point[1], point[2]], [nextPoint[1], nextPoint[2]], interpolationRatio);

                        interpolatedPoint.unshift(endDistance);
                        distanceFilteredPoints.push(interpolatedPoint);
                        return;
                    } else if (startDistance <= coordDistance && coordDistance <= endDistance) {
                        shouldFilterOut = false;
                    } else if (nextPoint) {
                        if (coordDistance < startDistance && nextPointDistance > startDistance) {
                            const interpolationRatio = (startDistance - coordDistance) / (nextPointDistance - coordDistance);
                            const interpolatedPoint = interpolate([point[1], point[2]], [nextPoint[1], nextPoint[2]], interpolationRatio);
                            interpolatedPoint.unshift(startDistance);

                            distanceFilteredPoints.push(interpolatedPoint);
                            return;
                        }
                    }
                });

                if (!shouldFilterOut) {
                    distanceFilteredPoints.push(point);
                }
            });

            return distanceFilteredPoints;
        });

        let segmentDistances = [];
        if (shortcutDistances) {
            segmentDistances = shortcutDistances;
        }

        if (exportDistances) {
            segmentDistances = exportDistances;
        }

        if (segmentDistances && segmentDistances.length) {
            const preExport = [];
            const exportSection = [];
            const postExport = [];

            sectionsToRender.forEach((sectionPoints) => {
                sectionPoints.forEach((point, idx) => {
                    const coordDistance = point[0];
                    const coord = [point[2], point[1]];
                    let distanceBefore = null;
                    let coordBefore = null;
                    const pointBefore = sectionPoints[idx - 1];

                    if (pointBefore) {
                        coordBefore = [pointBefore[2], pointBefore[1]];
                        distanceBefore = pointBefore[0];
                    }

                    if (coordDistance < segmentDistances[0]) {
                        preExport.push(coord);
                    } else if (coordDistance > segmentDistances[1]) {
                        if (distanceBefore !== null && coordDistance > segmentDistances[1] && distanceBefore < segmentDistances[1]) {
                            // interpolate beteween the two
                            const distanceRatio = (segmentDistances[1] - distanceBefore) / (coordDistance - distanceBefore);
                            const interpolatedCoord = interpolate(coordBefore, coord, distanceRatio);

                            postExport.push(interpolatedCoord);
                            exportSection.push(interpolatedCoord);
                        } else {
                            postExport.push(coord);
                        }
                    } else {
                        if (distanceBefore !== null && coordDistance > segmentDistances[0] && distanceBefore < segmentDistances[0]) {
                            // interpolate beteween the two
                            const distanceRatio = (segmentDistances[0] - distanceBefore) / (coordDistance - distanceBefore);
                            const interpolatedCoord = interpolate(coordBefore, coord, distanceRatio);

                            preExport.push(interpolatedCoord);
                            exportSection.push(interpolatedCoord);
                        } else {
                            exportSection.push(coord);
                        }
                    }
                });
            });

            const polylineRef1 = React.createRef();
            const polylineRef2 = React.createRef();
            const polylineRef3 = React.createRef();
            polylineRefs.current.push(polylineRef1, polylineRef2, polylineRef3);

            return [
                <Polyline
                    ref={polylineRef1}
                    key={`selectedroute--pre`}
                    color={lineColour}
                    weight={5}
                    positions={preExport}
                    onClick={onMouseClick}
                />,
                <Polyline
                    ref={polylineRef2}
                    key={`selectedroute--export`}
                    color={"#6C43DF"}
                    weight={5}
                    positions={exportSection}
                    onClick={onMouseClick}
                />,

                <Polyline
                    ref={polylineRef3}
                    key={`selectedroute--post`}
                    color={lineColour}
                    weight={5}
                    positions={postExport}
                    onClick={onMouseClick}
                />,
            ];
        } else {
            return sectionsToRender.map((section, idx) => {
                const allPoints = section.map((point) => [point[2], point[1]]);

                const newPolylineRef = React.createRef();
                polylineRefs.current.push(newPolylineRef);

                return (
                    <Polyline
                        ref={newPolylineRef}
                        key={`selectedroute-${idx}-single`}
                        color={lineColour}
                        weight={5}
                        positions={allPoints}
                        onClick={onMouseClick}
                    />
                );
            });
        }
    }, [routeCoordinates, shortcutDistances, exportDistances, videoDistanceSections, lineColour, onMouseClick]);

    return selectedPolyline;
};

export default SelectedRouteSegmentGeometryV2;
